/* In-app turn-by-turn for the Driver App. Keeps the driver inside ArmorBatch (so GPS keeps
   pinging dispatch) instead of handing off to Google Maps. Google Directions route, map
   follows the truck, big next-turn card, spoken call-outs, off-route recalculation. */
import { useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, useJsApiLoader, Polyline, Marker, TrafficLayer } from "@react-google-maps/api";
import { useDeviceHeading, RotateFrame } from "@/components/MapControls";
import { X, Navigation, CornerUpLeft, CornerUpRight, ArrowUp, Flag, RotateCcw, ExternalLink, Volume2, VolumeX, Sun, Moon, Compass } from "lucide-react";
import { speakNav } from "@/lib/voice";
import { api } from "@/lib/api";

const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
const GOOGLE_LIBRARIES = [];
const km = (a, b) => { const R = 6371000, p = Math.PI / 180, x = (b.lat - a.lat) * p, y = (b.lng - a.lng) * p; const h = Math.sin(x / 2) ** 2 + Math.cos(a.lat * p) * Math.cos(b.lat * p) * Math.sin(y / 2) ** 2; return 2 * R * Math.asin(Math.sqrt(h)); };
const strip = (html) => (html || "").replace(/<div[^>]*>/g, " — ").replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
const fmtDist = (m) => (m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.max(0, Math.round(m / 10) * 10)} m`);
const sayDist = (m) => (m >= 1000 ? `${(m / 1000).toFixed(1)} kilometres` : `${Math.round(m / 50) * 50} metres`);
const OFF_ROUTE_M = 80;

function TurnIcon({ maneuver, className }) {
  if (!maneuver) return <ArrowUp className={className} />;
  if (maneuver.includes("left")) return <CornerUpLeft className={className} />;
  if (maneuver.includes("right")) return <CornerUpRight className={className} />;
  if (maneuver.includes("uturn")) return <RotateCcw className={className} />;
  return <ArrowUp className={className} />;
}

const NIGHT_STYLE = [
  { elementType: "geometry", stylers: [{ color: "#1f2733" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#9aa6b8" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#121821" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#39465a" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#c3cddb" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#4a5a73" }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#2a3444" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#0e141c" }] },
  { featureType: "landscape.natural", elementType: "geometry", stylers: [{ color: "#1a222d" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
];
const DAY_STYLE = [{ featureType: "poi", stylers: [{ visibility: "off" }] }];

export default function DriverNav({ destination, label, onClose, onArrive, onFallback }) {
  const { isLoaded } = useJsApiLoader({ id: "gadtech-war-room", googleMapsApiKey: GOOGLE_KEY, libraries: GOOGLE_LIBRARIES });
  const [pos, setPos] = useState(null);
  const [heading, setHeading] = useState(0);
  const [route, setRoute] = useState(null);      // google DirectionsResult
  const [err, setErr] = useState("");
  const [muted, setMuted] = useState(false);
  const [night, setNight] = useState(() => localStorage.getItem("ab_nav_night") === "1");
  // Drivers expect the map to face the way they're driving, so heading-up is the
  // default here. Google ignores map.heading on raster roadmaps, so the canvas
  // is rotated in CSS instead — same approach as the Leaflet maps.
  const [headingUp, setHeadingUp] = useState(() => localStorage.getItem("ab_nav_heading_up") !== "0");
  const { heading: compass } = useDeviceHeading(headingUp);
  const [arrived, setArrived] = useState(false);
  const mapRef = useRef(null);
  const offRef = useRef(0);
  const lastRouteAt = useRef(0);
  const spoken = useRef({});

  useEffect(() => {
    if (!("geolocation" in navigator)) { setErr("No GPS on this device"); return undefined; }
    const ok = (p) => {
      setPos({ lat: p.coords.latitude, lng: p.coords.longitude });
      if (Number.isFinite(p.coords.heading) && p.coords.speed > 1) setHeading(p.coords.heading);
    };
    const bad = (e) => setErr(e.code === 1 ? "Location permission denied" : "Waiting for GPS…");
    navigator.geolocation.getCurrentPosition(ok, bad, { enableHighAccuracy: true, maximumAge: 10000, timeout: 15000 });
    const id = navigator.geolocation.watchPosition(ok, bad, { enableHighAccuracy: true, maximumAge: 2000, timeout: 20000 });
    return () => navigator.geolocation.clearWatch(id);
  }, []);

  const computeRoute = (origin) => {
    if (!origin) return;
    lastRouteAt.current = Date.now();
    const body = { origin_lat: origin.lat, origin_lng: origin.lng };
    if (typeof destination === "string") body.dest_address = destination; else { body.dest_lat = destination.lat; body.dest_lng = destination.lng; }
    api.post("/nav/route", body)
      .then((r) => { setRoute(r.data); setErr(""); spoken.current = {}; })
      .catch((e) => setErr(e.response?.data?.detail || "Route failed"));
  };
  useEffect(() => { if (pos && !route && !err) computeRoute(pos); }, [pos, route, err]); // eslint-disable-line react-hooks/exhaustive-deps

  const steps = route?.steps || [];
  const path = useMemo(() => (route?.path || []).map(([lat, lng]) => ({ lat, lng })), [route]);

  // Where are we along the route? Nearest step path point → current step; next turn = following step.
  const nav = useMemo(() => {
    if (!pos || !steps.length) return null;
    let best = { i: 0, d: Infinity };
    steps.forEach((s, i) => { for (const [lat, lng] of s.path || []) { const d = km(pos, { lat, lng }); if (d < best.d) best = { i, d }; } });
    const cur = steps[best.i];
    const next = steps[best.i + 1];
    const toTurn = km(pos, cur.end);
    let remaining = toTurn, secs = (cur.duration_s || 0) * (toTurn / Math.max(1, cur.distance_m || 1));
    for (let j = best.i + 1; j < steps.length; j++) { remaining += steps[j].distance_m || 0; secs += steps[j].duration_s || 0; }
    const dest = route.end;
    return { offRoute: best.d, cur, next, toTurn, remaining, secs, toDest: km(pos, dest), dest };
  }, [pos, steps, route]);

  // Off-route → recalc (3 fixes in a row, max every 20 s). Arrival + spoken call-outs.
  useEffect(() => {
    if (!nav) return;
    if (nav.offRoute > OFF_ROUTE_M) { offRef.current += 1; if (offRef.current >= 3 && Date.now() - lastRouteAt.current > 20000) { offRef.current = 0; if (!muted) speakNav("Recalculating."); computeRoute(pos); } }
    else offRef.current = 0;
    if (!arrived && nav.toDest < 50) { setArrived(true); if (!muted) speakNav("You have arrived at the site."); onArrive?.(); }
    const instr = strip(nav.next?.instruction || "");
    const key = `${nav.cur.start.lat}-${nav.cur.start.lng}`;
    if (instr && !muted) {
      if (nav.toTurn < 450 && nav.toTurn > 150 && !spoken.current[key + "far"]) { spoken.current[key + "far"] = 1; speakNav(`In ${sayDist(nav.toTurn)}, ${instr}`); }
      if (nav.toTurn <= 80 && !spoken.current[key + "near"]) { spoken.current[key + "near"] = 1; speakNav(instr); }
    }
  }, [nav, muted, arrived]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (mapRef.current && pos) mapRef.current.panTo(pos); }, [pos]);

  // Compass if the phone has one, GPS course over ground otherwise.
  const bearing = headingUp ? (compass || heading || 0) : 0;
  const upright = { transform: `rotate(${bearing}deg)` };
  const toggleNight = () => setNight((v) => { localStorage.setItem("ab_nav_night", v ? "0" : "1"); return !v; });
  const toggleHeading = () => setHeadingUp((v) => { localStorage.setItem("ab_nav_heading_up", v ? "0" : "1"); return !v; });

  const eta = nav ? new Date(Date.now() + nav.secs * 1000).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—";
  const gmaps = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(typeof destination === "string" ? destination : `${destination.lat},${destination.lng}`)}&travelmode=driving`;
  const nextText = nav ? strip(nav.next?.instruction || "") || "Continue to the site" : "";
  const nextMan = nav?.next?.maneuver || "";

  return (
    <div className="fixed inset-0 z-50 bg-[#0f1419] text-white flex flex-col" data-testid="driver-nav">
      {/* Next turn card */}
      <div className="shrink-0 bg-[#111827] border-b-4 border-[rgb(var(--primary))] px-4 py-3 flex items-center gap-4" data-testid="driver-nav-next">
        <div className="w-16 h-16 shrink-0 bg-[rgb(var(--primary))] text-black flex items-center justify-center"><TurnIcon maneuver={nextMan} className="w-10 h-10" /></div>
        <div className="min-w-0 flex-1">
          <div className="heading text-3xl font-black leading-none tabular-nums" data-testid="driver-nav-dist">{nav ? fmtDist(nav.toTurn) : (err || "Finding route…")}</div>
          <div className="text-base font-bold leading-tight mt-1 line-clamp-2" data-testid="driver-nav-instruction">{arrived ? "You have arrived" : nextText}</div>
        </div>
        <button type="button" onClick={onClose} data-testid="driver-nav-close" className="w-12 h-12 shrink-0 border border-white/30 flex items-center justify-center" aria-label="Exit navigation"><X className="w-6 h-6" /></button>
      </div>

      <div className="flex-1 min-h-0 relative">
        {isLoaded && pos ? (
          <RotateFrame bearing={bearing}>
          <GoogleMap mapContainerStyle={{ width: "100%", height: "100%" }} center={pos} zoom={17}
            onLoad={(m) => { mapRef.current = m; }}
            options={{ disableDefaultUI: true, gestureHandling: "greedy", mapTypeId: "roadmap", styles: night ? NIGHT_STYLE : DAY_STYLE, backgroundColor: night ? "#1f2733" : "#e9e9ea" }}>
            <TrafficLayer />
            {path.length > 0 && <Polyline path={path} options={{ strokeColor: "#1A73E8", strokeOpacity: 0.9, strokeWeight: 8 }} />}
            {nav && <Marker position={nav.dest} icon={{ path: window.google.maps.SymbolPath.CIRCLE, scale: 9, fillColor: "#F57C00", fillOpacity: 1, strokeColor: "#000", strokeWeight: 2 }} />}
            <Marker position={pos} icon={{ path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW, scale: 7, rotation: bearing ? 0 : heading, fillColor: "#22C55E", fillOpacity: 1, strokeColor: "#000", strokeWeight: 2 }} zIndex={10} />
          </GoogleMap>
          </RotateFrame>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center mono text-sm text-gray-400">{err || "Waiting for GPS fix…"}</div>
        )}
        <button type="button" onClick={() => { setErr(""); setRoute(null); }} data-testid="driver-nav-reroute" className="absolute top-3 left-3 h-12 px-3 bg-black/70 border border-white/20 flex items-center gap-2 mono text-[11px] uppercase tracking-widest"><RotateCcw className="w-4 h-4" /> Re-route</button>
        <button type="button" onClick={() => setMuted((m) => !m)} data-testid="driver-nav-mute" className="absolute top-3 right-3 w-12 h-12 bg-black/70 border border-white/20 flex items-center justify-center">{muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}</button>
        <button type="button" onClick={toggleNight} data-testid="driver-nav-night" aria-label={night ? "Switch to day map" : "Switch to night map"}
          className="absolute top-[4.25rem] right-3 w-12 h-12 bg-black/70 border border-white/20 flex items-center justify-center">
          {night ? <Sun className="w-5 h-5 text-amber-300" /> : <Moon className="w-5 h-5" />}
        </button>
        <button type="button" onClick={toggleHeading} data-testid="driver-nav-heading"
          className={`absolute bottom-3 right-3 w-14 h-14 border flex flex-col items-center justify-center ${headingUp ? "bg-[rgb(var(--primary))] text-black border-[rgb(var(--primary))]" : "bg-black/70 text-white border-white/20"}`}>
          <Compass className="w-6 h-6" style={headingUp ? upright : undefined} />
          <span className="mono text-[8px] uppercase tracking-widest font-black leading-none mt-0.5">{headingUp ? "Heading" : "North"}</span>
        </button>
      </div>

      {/* Bottom bar: ETA / remaining / destination + fallback */}
      <div className="shrink-0 bg-[#111827] border-t border-white/10 px-4 py-3 flex items-center gap-4" data-testid="driver-nav-footer">
        <div><div className="mono text-[10px] uppercase tracking-widest text-gray-400">ETA</div><div className="heading text-2xl font-black leading-none tabular-nums">{eta}</div></div>
        <div><div className="mono text-[10px] uppercase tracking-widest text-gray-400">Remaining</div><div className="heading text-2xl font-black leading-none tabular-nums">{nav ? fmtDist(nav.remaining) : "—"}</div></div>
        <div className="min-w-0 flex-1"><div className="mono text-[10px] uppercase tracking-widest text-gray-400 flex items-center gap-1"><Flag className="w-3 h-3" /> Site</div><div className="text-sm font-bold truncate">{label || "—"}</div></div>
        <a href={gmaps} target="_blank" rel="noreferrer" data-testid="driver-nav-gmaps" onClick={onFallback} title="Fallback — note: Google Maps pauses our GPS" className="shrink-0 w-12 h-12 border border-white/20 flex items-center justify-center text-gray-300"><ExternalLink className="w-5 h-5" /></a>
      </div>
      <div className="shrink-0 bg-emerald-600 text-white text-center mono text-[11px] py-1 flex items-center justify-center gap-2"><Navigation className="w-3 h-3" /> GPS live — dispatch can see you while you navigate here</div>
    </div>
  );
}
